import { fetch } from '@/middleware';
import Constants from '@/utils/constants';
import store from '../store/index';
import cacheService from '@/service/cacheService.js';

export async function getPasswordForShowLink(showLink) {
  const passwordDetails = Constants.Api.ShowPassword.replace('{showLink}', showLink);
  return fetch.get(passwordDetails);
};

export async function getConferenceCallDetails(showLink, confId) {
  if (!!showLink) {
    let conferenceCallDetailsPath = `${Constants.Api.ConferenceCallDetails}?showLink=${showLink}`
    if(confId){
      conferenceCallDetailsPath = `${Constants.Api.ConferenceCallDetails}?showLink=${showLink}&confId=${confId}`
    }    
    return fetch.get(conferenceCallDetailsPath);
  }
};

export async function login(user) {
  return fetch.post(Constants.Api.Login, 'post', user)
};

export async function saveEntryCode (emailAddress, param) {
  const url = Constants.Api.SaveEntryCode.replace('{email}', emailAddress)
  return fetch.post(url, 'post', param);
};

export async function changePassword(payload) {
  const {password, token } = payload;
  const apiUrl = Constants.Api.ResetPassword.replace('{token}', token);
  return fetch.post(apiUrl, 'post', {password});
};

export async function getUserRegistrationStatus({ emailAddress, showLink, confId, canBypassRegistration }) {
  let userRegistrationStatusPath = Constants.Api.UserRegistrationStatus.replace('{emailAddress}', emailAddress);
  if (showLink) {
    userRegistrationStatusPath += `&showLink=${showLink}`;
    if(confId) {
      userRegistrationStatusPath += `&confId=${confId}`;
    }
    if (canBypassRegistration) {
      userRegistrationStatusPath += `&suppressConfEmail=${canBypassRegistration}`;
    }
  }

  return fetch.get(userRegistrationStatusPath);
};

export async function saveNewUser(param, isSGM=false) {
  const url = isSGM ? Constants.Api.RegisterNewUserUrl + '&isSGM=true' : Constants.Api.RegisterNewUserUrl;
  return fetch.post(url, 'post', param);
};

export async function saveUserConsentToPolicy(param) {
  const url = Constants.Api.saveConsentToPolicyUrl;
  return fetch.post(url, 'post', param);
};

export async function saveSupportRequest(param) {
  return fetch.post(Constants.Api.SaveTechSupport, 'post', param);
};

export async function getUserPermission() {
  return fetch.get(Constants.Api.UserPermission);
};

export async function getAccessCodes(presId, confId, replay = false) {
  const path = Constants.Api.AccessCodes.replace('{presId}', presId).replace('{confId}', confId).replace('{replay}', replay);
  return fetch.get(path);
};

export async function logout() {
  return fetch.post(Constants.Api.Logout);
};

export async function statusAndProcessEntryCode(params) {
  const auth = {
    headers: { 'Content-Type': 'application/json' }
  };

  return fetch.post(Constants.Api.StatusAndProcessEntryCode, "post", params, auth);
}

export async function getDisclaimers(presId) {
    let disclaimersPath = Constants.Api.Disclaimers.replace('{presId}', presId);
    return fetch.get(disclaimersPath);
};

export async function getDisclaimerContent(presId, disclaimerId) {
  if (!!presId && !!disclaimerId) {
    const disclaimerPath = Constants.Api.Disclaimer.replace('{presId}', presId).replace('{disclaimerId}', disclaimerId);
    return fetch.get(disclaimerPath);
  }
};

export async function confirmRegisteredUserEmail(rowGuid) {
  const confirmUserEmailPath = Constants.Api.ConfirmUserEmail.replace('{userRowGuid}', rowGuid);
  return fetch.post(confirmUserEmailPath, 'post');
};

export async function confirmEmailDomain(emailAddress) {
  const confirmEmailDomainPath = Constants.Api.ConfirmEmailDomain.replace('{emailAddress}', emailAddress);
  return fetch.post(confirmEmailDomainPath, 'post');
}

export async function getLoggedInUserForPresentation(showLoader = true) {
  const getLoggedInUserPath = Constants.Api.GetLoggedInUserForPresentation;
  return fetch.get(getLoggedInUserPath, {}, {}, showLoader);
};

export async function getRegisteredUserForConferenceCall(presId, confId, params, watch_request) {
  const getRegisteredUserPath = Constants.Api.GetRegisteredUserForConferenceCall.replace('{presId}', presId).replace('{confId}', confId);
  const cacheKey = `${Constants.CacheKeysPrefix.ParticipantData}-${confId}`;
  const cachedParticipantData = cacheService.getCacheData(cacheKey);

  if (!cachedParticipantData) {
    const response = fetch.get(getRegisteredUserPath, params, {}, watch_request);
    cacheService.setCacheData(cacheKey, response, 55);
    return response;
  }
  return cachedParticipantData;  
};

export async function getUserForLiveEventConferenceCall(params, watch_request) {
  const response = fetch.get(Constants.Api.GetUserForLiveEvent, params, {}, watch_request);
  cacheService.setCacheData(cacheKey, response, 55);
  return response;
};

export async function registerUserForConferenceCall(presId, confId, params) {
  const registerUserPath = Constants.Api.RegisterForConferenceCall.replace('{presId}', presId).replace('{confId}', confId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };

  return fetch.post(registerUserPath, 'post', params, auth);
};

export async function updateUserForConferenceCall(presId, confId, participantId, params) {
  const registerUserPath = Constants.Api.UpdateRegisteredUserForConferenceCall.replace('{presId}', presId).replace('{confId}', confId).replace('{ParticipantId}', participantId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };

  return fetch.post(registerUserPath, 'put', params, auth);
};

export async function getDateTimeNow() {
  return fetch.get(Constants.Api.DateTimeNowUrl);
}

export async function getConferenceCallStatus(presId, conferenceCallId, showLoader) {
  const conferenceCallStatusUrl = Constants.Api.ConferenceCallStatusUrl.replace('{presId}', presId).replace('{conferenceCallId}', conferenceCallId);
  const cacheKey = `${Constants.CacheKeysPrefix.ConferenceStatus}-${conferenceCallId}`;
  const cachedConferenceStatus = cacheService.getCacheData(cacheKey);
  
  if (!cachedConferenceStatus) {
    const response = fetch.get(conferenceCallStatusUrl, {}, {}, showLoader);
    cacheService.setCacheData(cacheKey, response, 40);
    return response;
  }

  return cachedConferenceStatus;
}

export async function getUserInformation(emailAddress, showLink) {
  const userInformationUrl = Constants.Api.UserInformation.replace('{emailAddress}', emailAddress)
    .replace('{showLink}', showLink);
  return fetch.get(userInformationUrl);
}

export async function getCountries() {
  const countriesUrl = Constants.Api.Countries;
  return fetch.get(countriesUrl);
}



export async function getIcsFileForConferenceCall(presId, conferenceCallId){
  const icsFileDownloadUrl = Constants.Api.downloadIcsFile.replace('{presId}', presId).replace('{confId}', conferenceCallId);
  return fetch.get(icsFileDownloadUrl);
};

export async function fetchLiveStreamUrl(presId, conferenceCallId, watch_request){
  const liveStreamUrl = Constants.Api.liveStreamUrl.replace('{presId}', presId).replace('{confId}', conferenceCallId);
  return fetch.get(liveStreamUrl, {}, {}, watch_request);
};

export async function fetchReplayAudio(presId, conferenceCallId){
  const replayAudioUrl = Constants.Api.replayAudioUrl.replace('{presId}', presId).replace('{confId}', conferenceCallId);
  return fetch.get(replayAudioUrl);
};

export async function setParticipantSession(presId, conferenceCallId, payload){
  const participantSessionUrl = Constants.Api.participantSession.replace('{presId}', presId).replace('{confId}', conferenceCallId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.post(participantSessionUrl, 'post', payload, auth, false);
};

export async function endParticipantSession(presId, conferenceCallId, payload){
  const participantSessionUrl = Constants.Api.participantSession.replace('{presId}', presId).replace('{confId}', conferenceCallId) + '/' + payload.Id;
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  var client = new XMLHttpRequest();
  client.open("PUT", participantSessionUrl, false); // third parameter indicates sync xhr
  client.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
  client.setRequestHeader("Accept", "application/json, text/plain, */*");
  client.setRequestHeader("AntiForgeryToken", AntiForgeryToken);
  client.send(JSON.stringify(payload)); 
  if(client.status === 200){
    return;
  }
};

export function livestreamEvents(presId, conferenceCallId, payload){
  const livestreamEventsUrl = Constants.Api.livestreamEvents.replace('{presId}', presId).replace('{confId}', conferenceCallId).replace('{ParticipantId}', payload.ParticipantId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  fetch.post(livestreamEventsUrl, 'post', payload, auth, false);
};

export async function hasConsentedToPrivacyPolicy(emailAddress) {
  const privacyPolicyConsentUrl = Constants.Api.checkConsentToPolicyUrl.replace('{email}', emailAddress);
  return fetch.get(privacyPolicyConsentUrl);
}

export async function getConferenceCallGlobalPhoneDirectory(confId) {

  let getGlobalPhoneNumberUrlByConfId = `${Constants.Api.ConferenceCallGlobalPhoneDirectory}`;

  if(confId){
     getGlobalPhoneNumberUrlByConfId = `${Constants.Api.ConferenceCallGlobalPhoneDirectory}?confId=${confId}`;
  }

  return fetch.get(getGlobalPhoneNumberUrlByConfId);
}

export async function getSGMDialInDetails(presId, sgmMeetingId) {
  const sgmDialInDetailsUrl = Constants.Api.GetSGMDialInDetails.replace('{presId}', presId).replace('{meetingId}', sgmMeetingId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.get(sgmDialInDetailsUrl, auth);
}

export async function getMGMDialInDetails(presId) {
  const mgmDialInDetailsUrl = Constants.Api.GetMGMDialInDetails.replace('{presId}', presId);
  return fetch.get(mgmDialInDetailsUrl);
}

export async function getUserStatus(presId, conferenceCallId, showLoader) {
  const UserStatusUrl = Constants.Api.UserStatusUrl.replace('{presId}', presId).replace('{conferenceCallId}', conferenceCallId);
  const cacheKey = `${Constants.CacheKeysPrefix.UserConferenceStatus}-${conferenceCallId}`;
  const cachedUserStatus = cacheService.getCacheData(cacheKey);
  
  if (!cachedUserStatus) {
    const response = fetch.get(UserStatusUrl, {}, {}, showLoader);
    cacheService.setCacheData(cacheKey, response, 40);
    return response;
  }
  return cachedUserStatus;
}

export async function getQuestions(presId, conferenceCallId, type, showLoader) {
  const QuestionsUrl = `${Constants.Api.GetSubmitQuestion.replace('{presId}', presId).replace('{conferenceCallId}', conferenceCallId)}?questionType=${type}`;
  const cacheKey = `${Constants.CacheKeysPrefix.QuestionsQueue}-${conferenceCallId}`;
  const cachedQuestions = cacheService.getCacheData(cacheKey);
  
  if (!cachedQuestions) {
    const response = fetch.get(QuestionsUrl, {}, {}, showLoader);
    cacheService.setCacheData(cacheKey, response, 9);
    return response;
  }
  
  return cachedQuestions;

}

export async function submitQuestion(presId, conferenceCallId, question, showLoader) {
  const QuestionsUrl = Constants.Api.GetSubmitQuestion.replace('{presId}', presId).replace('{conferenceCallId}', conferenceCallId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.post(QuestionsUrl, 'post', question, auth, showLoader);
}

export async function updateQuestion(presId, conferenceCallId, questionId, question, showLoader) {
  const QuestionsUrl = Constants.Api.EditDeleteQuestion.replace('{presId}', presId)
    .replace('{conferenceCallId}', conferenceCallId)
    .replace('{questionId}', questionId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];  
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.post(QuestionsUrl, 'put', question, auth, showLoader);
}

export async function deleteQuestion(presId, conferenceCallId, questionId, showLoader) {
  const QuestionsUrl = Constants.Api.EditDeleteQuestion.replace('{presId}', presId)
    .replace('{conferenceCallId}', conferenceCallId)
    .replace('{questionId}', questionId);

  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.delete(QuestionsUrl, auth, showLoader);

}

export async function verifyUserInformation(emailAddress) {
  let verifyUserInformationUrl = Constants.Api.VerifyUserInformation.replace('{emailAddress}', emailAddress)
  return fetch.get(verifyUserInformationUrl);
}

export async function getCompanySuggestions(emailAddress) {
  let companysuggestionsUrl = Constants.Api.CompanySuggestions.replace('{emailAddress}', emailAddress)
  return fetch.get(companysuggestionsUrl);
}

export async function completeUserProfile(param, isSGM=false) {
  const url = isSGM ? Constants.Api.CompleteProfile + '&isSGM=true' : Constants.Api.CompleteProfile;
  return fetch.post(url, 'post', param);
}

export async function resendVerficationEmail(presId, confId, emailAddress, showLink, listenOnly) {
  const resendVerficationEmailUrl = Constants.Api.resendVerificationEmail.replace('{presId}', presId)
    .replace('{confId}', confId).replace('{emailAddress}', emailAddress).replace('{showLink}', showLink).replace('{listenOnly}', listenOnly);
  return fetch.post(resendVerficationEmailUrl, 'post');
}

export async function resendSGMVerificationEmail(presId, emailAddress, showLink) {
  const resendSGMVerificationEmailUrl = Constants.Api.resendSGMVerificationEmail.replace('{presId}', presId)
    .replace('{emailAddress}', emailAddress).replace('{showLink}', showLink);
  return fetch.post(resendSGMVerificationEmailUrl, 'post');
}

export async function updateMetadata(emailAddress, showLink) {
  return fetch.post(Constants.Api.updateMetadata.replace('{emailAddress}', emailAddress).replace('{showLink}', showLink), "put", {"Key":"ShowedConferencePostRegistrationAlert"});
};

export async function getReleaseInfo() {
  const auth = {
    headers: { 'ApplicationId': '32' }
  };
  return fetch.get(Constants.Api.GetReleaseInfo, {}, auth, true);
};

export async function getAccountTypes() {
  const accountTypesUrl = Constants.Api.AccountTypes;
  return fetch.get(accountTypesUrl);
}

export async function getCompanyRoles(accountTypeId, showLoader) {
  const companyRolesUrl = Constants.Api.CompanyRoles.replace('{accountTypeId}', accountTypeId);
  return fetch.get(companyRolesUrl, {}, {}, showLoader);
}

export async function getPresenterRegBypass(presId, emailAddress, confId) {
  let verifyPresenterUrl = Constants.Api.VerifyPresenterForLiveVideo.replace('{presId}', presId).replace('{emailAddress}', emailAddress);
  if(confId){
    verifyPresenterUrl = verifyPresenterUrl.concat(`&confId=${confId}`);
  } else {

  }
  return fetch.get(verifyPresenterUrl);
}

export async function convertUTCStringToLocalTimezone(utcDateString, dateFormat) {
  const url = Constants.Api.convertUtcToLocalTimezoneUrl
  .replace('{utcString}', utcDateString)
  .replace('{dateFormat}', dateFormat)
  .replace('{utcDateFormat}', 'yyyy-MM-ddTHH:mm:ss')
  .replace('{ianaTimezone}', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return fetch.get(url, {}, {}, false);
}

export async function getAttendeeUrl(presId) {
  const url = Constants.Api.GetAttendeeUrl
  .replace('{presId}', presId);
  const { AntiForgeryToken } = store.getters['login/GET_LOGGED_IN_USER'];
  const auth = {
    headers: { 'AntiForgeryToken': AntiForgeryToken }
  };
  return fetch.post(url, 'post', {}, auth, false);
};

export async function getSGMMeetingJoinLink(presId, sgmMeetingId, showLink, coreId) {
  const url = Constants.Api.getSGMMeetingJoinLink
    .replace('{presId}', presId)
    .replace('{meetingId}', sgmMeetingId)
    .replace('{showId}', showLink)
    .replace('{coreId}', coreId ? coreId : 'null');
  return fetch.get(url, {}, {}, false);
}

export async function getPresentationRouteUrl(presInfo) {
  const url = Constants.Api.getPresentationRouteUrl
    .replace('{presId}', presInfo.presId) 
    .concat(`?showId=${presInfo.showId}`)
    .concat(`&confId=${presInfo.confId}`);
  return fetch.get(url, {}, {}, false);
}

export async function getBranding(brandingGuid, appId) {
  const url = Constants.Api.getBrandingUrl.replace('{brandingGuid}', brandingGuid).replace('{appId}', appId);
  return fetch.get(url);
}

export async function getBrandingInfo(brandingGuid) {
  const url = Constants.Api.getBrandingInfoUrl.replace('{brandingGuid}', brandingGuid);
  return fetch.get(url);
}

export async function getBrandingLogos(presId) {
  const url = Constants.Api.getBrandingLogosUrl.replace('{presId}', presId);
  return fetch.get(url);
}